import React, { useState } from 'react';
import { withFraudWriteAccess } from 'contexts/adminAccess';
import { Form } from 'react-final-form';
import { useDataProvider, useRefresh } from 'react-admin';
import AddIcon from '@material-ui/icons/Add';
import { FORM_ERROR } from 'final-form';
import { makeStyles, Typography } from '@material-ui/core';
import ActionButton from 'components/buttons/ActionButton';
import { Dialog } from 'components/common';
import { Input } from 'components/form';
import { required, phoneNumber, composeValidators } from 'components/form/validationRules';

const useStyles = makeStyles((theme) => ({
  error: {
    color: theme.palette.error.main,
  },
}));

interface FormValues {
  phoneNumber: string;
  reason: string;
}

const AddBlockedPhoneButton = (props) => {
  const classes = useStyles();

  const dataProvider = useDataProvider();
  const refresh = useRefresh();

  const [open, setOpen] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setError('');
  };

  const handleAddPhone = async (values: FormValues) => {
    setError('');

    try {
      const { data } = await dataProvider.post('blocklist', {
        data: { ...values },
      });
      refresh();
      handleCloseDialog();
      return data;
    } catch (error: any) {
      if (typeof error === 'string') {
        return { [FORM_ERROR]: error };
      }

      const errorData = JSON.parse(error.message);
      if (errorData?.errors && Array.isArray(errorData.errors)) {
        const formErrors = errorData.errors.reduce((acc, err) => {
          if (err.field) {
            // If field already has errors, append new error with a comma
            if (acc[err.field]) {
              acc[err.field] = `${acc[err.field]}, ${err.description}`;
            } else {
              acc[err.field] = err.description;
            }
          }
          return acc;
        }, {});

        const errorString = Object.values(formErrors).join(', ');

        setError(errorString);
        return { [FORM_ERROR]: formErrors };
      }

      return { [FORM_ERROR]: error?.message };
    }
  };

  return (
    <>
      {open && (
        <Form<FormValues>
          onSubmit={handleAddPhone}
          render={({ handleSubmit, submitting }) => {
            return (
              <Dialog
                title="Block phone number"
                onConfirm={handleSubmit}
                onCancel={handleCloseDialog}
                open={open}
                submitting={submitting}>
                <Input
                  name="phoneNumber"
                  label="Phone"
                  validate={composeValidators(required, phoneNumber)}
                  error={error}
                  helperText={error}
                />
                <Input
                  name="reason"
                  label="Reason"
                  validate={required}
                  error={error}
                  helperText={error}
                />
                {error && <Typography className={classes.error}>{error}</Typography>}
              </Dialog>
            );
          }}
        />
      )}
      <ActionButton onClick={handleOpenDialog} startIcon={<AddIcon />}>
        Add phone
      </ActionButton>
    </>
  );
};

export default withFraudWriteAccess(AddBlockedPhoneButton);
